
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Eye, EyeOff, Copy, RefreshCw, Trash2, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const ApiSettings = () => {
  const [showToken, setShowToken] = useState(false);
  
  // Mock API data
  const apiTokens = [
    { id: 1, name: 'Website Integration', token: 'toro_api_xxxxxxxxxxxxxxxxxxxxxx', created: '2023-10-15', lastUsed: '2023-12-10', status: 'active' },
    { id: 2, name: 'Mobile App', token: 'toro_api_xxxxxxxxxxxxxxxxxxxxxx', created: '2023-11-20', lastUsed: '2023-12-05', status: 'active' },
    { id: 3, name: 'Analytics Tool', token: 'toro_api_xxxxxxxxxxxxxxxxxxxxxx', created: '2023-09-05', lastUsed: '2023-11-30', status: 'expired' },
  ];
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Would typically show a toast notification here
      console.log('Copied to clipboard');
    });
  };
  
  return (
    <div className="max-w-4xl">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>API Settings</CardTitle>
          <CardDescription>
            Manage API access tokens for integrating with other services.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-muted-foreground">
            These tokens provide access to the TORO API. Treat them as sensitive as passwords and never share them publicly.
          </p>
          
          <Button className="mb-6">
            <Plus className="h-4 w-4 mr-2" />
            Generate New API Token
          </Button>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Token</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiTokens.map((token) => (
                <TableRow key={token.id}>
                  <TableCell>{token.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
                        {showToken ? token.token : `${token.token.substring(0, 15)}...`}
                      </code>
                      <button
                        onClick={() => setShowToken(!showToken)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {showToken ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(token.token)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell>{token.created}</TableCell>
                  <TableCell>{token.lastUsed}</TableCell>
                  <TableCell>
                    <Badge variant={token.status === 'active' ? 'default' : 'outline'}>
                      {token.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon">
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Webhook Configuration</CardTitle>
          <CardDescription>
            Configure webhooks to receive real-time updates when events occur.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <div className="grid w-full max-w-sm items-center gap-1.5">
              <label htmlFor="webhookUrl" className="text-sm font-medium">Webhook URL</label>
              <Input 
                id="webhookUrl" 
                type="url" 
                placeholder="https://your-app.com/webhook" 
              />
            </div>
            
            <div>
              <p className="text-sm font-medium mb-2">Events to trigger webhook</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="content_published" className="h-4 w-4" />
                  <label htmlFor="content_published" className="text-sm">Content Published</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="content_updated" className="h-4 w-4" />
                  <label htmlFor="content_updated" className="text-sm">Content Updated</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="user_registered" className="h-4 w-4" />
                  <label htmlFor="user_registered" className="text-sm">User Registered</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="form_submitted" className="h-4 w-4" />
                  <label htmlFor="form_submitted" className="text-sm">Form Submitted</label>
                </div>
              </div>
            </div>
            
            <div className="pt-2">
              <Button>Save Webhook Configuration</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApiSettings;
